"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronsDown, ArrowUpRight, Loader2, Check, Wallet, X, AlertCircle, ArrowRight, Ticket } from "lucide-react"
import Image from "next/image"
import { useAccount, useBalance, useChainId, useSwitchChain, useReadContract } from "wagmi"
import { useWallet } from "@/lib/wallet-context"
import { formatEther, parseEther, parseUnits, formatUnits } from "viem"
import { useTKSPresale, useTKSContributeNative, useTKSContributeToken, useTokenApproval } from "@/lib/tks-presale-hooks"
import { useTKSClaimVault } from "@/lib/tks-claim-hooks"
import { STABLECOIN_ADDRESSES, ERC20_ABI } from "@/lib/tks-presale-abi"
import { TKS_TOKEN_ADDRESS } from "@/lib/tks-claim-vault-abi"
import { getCryptoPrice } from "@/lib/price-service"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogClose } from "@/components/ui/dialog"
import { motion, AnimatePresence } from "framer-motion"
import { Sparkles, PartyPopper, ArrowLeft, Activity, ExternalLink } from "lucide-react"
import { useSolanaTransfer } from "@/lib/hooks/solana-transfer-hook"
import { useTronTransfer } from "@/lib/hooks/tron-transfer-hook"
// TronWallet Adapter for context-aware wallet connection
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
// Custom Tron modal that uses AppKit for WalletConnect
import { TronWalletModal } from './tron-wallet-modal'
// Custom TON modal for TON Connect wallets
import { TonWalletModal } from './ton-wallet-modal'
// AppKit network switching — needed to set correct namespace before WC connect
import { useAppKitNetwork } from '@reown/appkit/react'
import { customSolanaNetwork, activeTronNetwork, activeTonNetwork } from '@/lib/appkit-init'

// TKS Static Configuration — driven by env vars
const TKS_STATIC = {
    listingPrice: parseFloat(process.env.NEXT_PUBLIC_TKS_LISTING_PRICE || "0.10"),
    stageTarget: parseInt(process.env.NEXT_PUBLIC_TKS_HARD_CAP_USD || "16300000"),
    currentStage: parseInt(process.env.NEXT_PUBLIC_TKS_STAGE || "1"),
}

// Network configurations - driven by env NEXT_PUBLIC_NETWORK_MODE
const isMainnet = process.env.NEXT_PUBLIC_NETWORK_MODE === 'mainnet'

interface NetworkConfig {
    chainId: number | string
    name: string
    icon: string
    short: string
    native: string
    isNonEvm?: boolean
}

const NETWORKS: NetworkConfig[] = isMainnet ? [
    // EVM Mainnet Chains
    { chainId: 56, name: "BNB Chain", icon: "/logos/bnb-bnb-logo.svg", short: "BSC", native: "BNB" },
    { chainId: 1, name: "Ethereum", icon: "/logos/ethereum-eth-logo.svg", short: "ETH", native: "ETH" },
    { chainId: 137, name: "Polygon", icon: "/logos/polygon-matic-logo.svg", short: "POL", native: "POL" },
    { chainId: 8453, name: "Base", icon: "/icons8/icons/base-chain.webp", short: "BASE", native: "ETH" },
    // Non-EVM Chains
    { chainId: "solana", name: "Solana", icon: "/logos/solana-sol-logo.svg", short: "SOL", native: "SOL", isNonEvm: true },
    { chainId: "tron", name: "Tron", icon: "/logos/tron-trx-logo.svg", short: "TRX", native: "TRX", isNonEvm: true },
    { chainId: "ton", name: "TON", icon: "/logos/toncoin-ton-logo.svg", short: "TON", native: "TON", isNonEvm: true },
] : [
    // EVM Testnet Chains
    { chainId: 97, name: "BSC Testnet", icon: "/logos/bnb-bnb-logo.svg", short: "BSC", native: "BNB" },
    { chainId: 11155111, name: "Sepolia", icon: "/logos/ethereum-eth-logo.svg", short: "ETH", native: "ETH" },
    { chainId: 80002, name: "Polygon Amoy", icon: "/logos/polygon-matic-logo.svg", short: "MATIC", native: "MATIC" },
    { chainId: 84532, name: "Base Sepolia", icon: "/icons8/icons/base-chain.webp", short: "BASE", native: "ETH" },
    // Non-EVM Chains
    { chainId: "solana", name: "Solana Testnet", icon: "/logos/solana-sol-logo.svg", short: "SOL", native: "SOL", isNonEvm: true },
    { chainId: "tron", name: "Shasta Testnet", icon: "/logos/tron-trx-logo.svg", short: "TRX", native: "TRX", isNonEvm: true },
    { chainId: "ton", name: "TON Testnet", icon: "/logos/toncoin-ton-logo.svg", short: "TON", native: "TON", isNonEvm: true },
]

// Pay tokens per network (EVM uses number, non-EVM uses string)
const PAY_TOKENS: Record<number | string, { name: string; symbol: string; icon: string; isNative: boolean; decimals: number }[]> = {
    // EVM Testnet Networks
    97: [
        { name: "BNB", symbol: "BNB", icon: "/logos/bnb-bnb-logo.svg", isNative: true, decimals: 18 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 18 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 18 },
    ],
    11155111: [
        { name: "ETH", symbol: "ETH", icon: "/logos/ethereum-eth-logo.svg", isNative: true, decimals: 18 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 6 },
    ],
    80002: [
        { name: "MATIC", symbol: "MATIC", icon: "/logos/polygon-matic-logo.svg", isNative: true, decimals: 18 },
    ],
    84532: [
        { name: "ETH", symbol: "ETH", icon: "/logos/ethereum-eth-logo.svg", isNative: true, decimals: 18 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 6 },
    ],
    // EVM Mainnet Networks
    56: [
        { name: "BNB", symbol: "BNB", icon: "/logos/bnb-bnb-logo.svg", isNative: true, decimals: 18 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 18 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 18 },
    ],
    1: [
        { name: "ETH", symbol: "ETH", icon: "/logos/ethereum-eth-logo.svg", isNative: true, decimals: 18 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 6 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 6 },
    ],
    137: [
        { name: "POL", symbol: "POL", icon: "/logos/polygon-matic-logo.svg", isNative: true, decimals: 18 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 6 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 6 },
    ],
    8453: [
        { name: "ETH", symbol: "ETH", icon: "/logos/ethereum-eth-logo.svg", isNative: true, decimals: 18 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 6 },
    ],
    // Non-EVM Networks
    solana: [
        { name: "SOL", symbol: "SOL", icon: "/logos/solana-sol-logo.svg", isNative: true, decimals: 9 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 6 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 6 },
    ],
    tron: [
        { name: "TRX", symbol: "TRX", icon: "/logos/tron-trx-logo.svg", isNative: true, decimals: 6 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 6 },
        { name: "USDC", symbol: "USDC", icon: "/logos/usd-coin-usdc-logo.svg", isNative: false, decimals: 6 },
    ],
    ton: [
        { name: "TON", symbol: "TON", icon: "/logos/toncoin-ton-logo.svg", isNative: true, decimals: 9 },
        { name: "USDT", symbol: "USDT", icon: "/logos/tether-usdt-logo.svg", isNative: false, decimals: 6 },
    ],
}

interface RawTransaction {
    hash: string
    user: string
    payAmount: string
    payToken: string
    usdValue: string
    tksAmount: string
    chainId: number
    chainName: string
    explorer: string
}

export function TKSPresaleBoxV2() {
    const [mounted, setMounted] = useState(false)
    // Use wagmi for EVM-specific operations (address for EVM contracts, chain info)
    const { address: evmAddress } = useAccount()
    const chainId = useChainId()
    const { switchChain } = useSwitchChain()
    // Get connection state and balance from wallet context - works for both EVM and Solana
    const { isConnected, address: walletAddress, connectWallet, disconnectWallet, balance: walletContextBalance, network: walletNetwork, wcTronAddress, wcSolanaAddress, wcTonAddress, refreshBalance } = useWallet()

    // Custom Tron modal state - for context-aware Connect Wallet button
    const [tronModalOpen, setTronModalOpen] = useState(false)
    // Custom TON modal state - for context-aware Connect Wallet button
    const [tonModalOpen, setTonModalOpen] = useState(false)

    // AppKit network switcher — set correct namespace before WC connect
    const { switchNetwork: switchCaipNetwork } = useAppKitNetwork()

    // Track if a non-EVM network is currently selected (prevents EVM sync from overriding)
    const [isNonEvmSelected, setIsNonEvmSelected] = useState(false)

    // UI state
    const [networkOpen, setNetworkOpen] = useState(false)
    const [payTokenOpen, setPayTokenOpen] = useState(false)
    const [payAmount, setPayAmount] = useState("")
    const [receiveAmount, setReceiveAmount] = useState("")
    const [cryptoPrice, setCryptoPrice] = useState<number>(0)
    const [howToBuyOpen, setHowToBuyOpen] = useState(false)
    const [claimNetworkOpen, setClaimNetworkOpen] = useState(false)
    const [communityTxs, setCommunityTxs] = useState<RawTransaction[]>([])
    const [showNetworkSwitchModal, setShowNetworkSwitchModal] = useState(false)
    const [pendingNetwork, setPendingNetwork] = useState<typeof NETWORKS[0] | null>(null)

    // Promo code state
    const [promoCode, setPromoCode] = useState("")
    const [promoInput, setPromoInput] = useState("")
    const [promoValid, setPromoValid] = useState(false)
    const [promoBonus, setPromoBonus] = useState(0)
    const [promoDescription, setPromoDescription] = useState("")
    const [promoLoading, setPromoLoading] = useState(false)
    const [promoError, setPromoError] = useState("")
    const [promoExpanded, setPromoExpanded] = useState(false)

    // Optimistic tracking for sync gaps (Wei)
    const [optimisticContributionDeltaWei, setOptimisticContributionDeltaWei] = useState<bigint>(BigInt(0))
    const [optimisticClaimDeltaWei, setOptimisticClaimDeltaWei] = useState<bigint>(BigInt(0))

    // Flag to prevent sync effect from overriding manual network selection during chain switch
    const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false)

    // Internal flow state: 'idle' | 'awaiting' | 'processing' | 'success'
    const [buyFlowStep, setBuyFlowStep] = useState<'idle' | 'awaiting' | 'processing' | 'success'>('idle')
    const [isBuyFlowActive, setIsBuyFlowActive] = useState(false)
    const [tksPresaleId, setTksPresaleId] = useState<string | null>(null)
    const [lastPurchase, setLastPurchase] = useState<{ amount: string, currency: string, tokenAmount: string } | null>(null)

    // Claim flow state: 'idle' | 'processing' | 'success' | 'failed'
    const [claimFlowStep, setClaimFlowStep] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle')
    const [lastClaimedAmount, setLastClaimedAmount] = useState<number>(0)
    const [claimErrorMessage, setClaimErrorMessage] = useState<string>('')
    // Separate state for success animation to prevent Framer Motion from unmounting prematurely
    const [showClaimSuccess, setShowClaimSuccess] = useState<boolean>(false)
    const claimTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const hasHandledClaimSuccess = useRef<boolean>(false)

    // Non-EVM claim address state (for Solana/Tron/TON contributors)
    const [claimAddress, setClaimAddress] = useState("")
    const [claimAddressSaving, setClaimAddressSaving] = useState(false)
    const [claimAddressSaved, setClaimAddressSaved] = useState(false)
    const [claimAddressError, setClaimAddressError] = useState<string | null>(null)

    // Select claim network - defaults to BSC Testnet
    const [selectedClaimNetwork, setSelectedClaimNetwork] = useState(NETWORKS[0]) // BSC Testnet is index 0

    // Selected network state - updates UI immediately, then triggers wallet switch
    const [selectedNetwork, setSelectedNetwork] = useState(NETWORKS[0])
    const availableTokens = PAY_TOKENS[selectedNetwork.chainId] || PAY_TOKENS[NETWORKS[0].chainId]
    const [selectedPayToken, setSelectedPayToken] = useState(availableTokens[0])

    // Use WC session address when user manually selects a non-EVM network
    // but the wallet context's finalAddress is still EVM (WC limitation)
    // IMPORTANT: Don't fall back to walletAddress for non-EVM — it would be the wrong chain's address
    const address = useMemo(() => {
        if (selectedNetwork?.chainId === 'tron') {
            return wcTronAddress || null  // Don't use Solana/EVM address for Tron
        }
        if (selectedNetwork?.chainId === 'ton') {
            return wcTonAddress || null  // Don't use Solana/EVM address for TON
        }
        if (selectedNetwork?.chainId === 'solana') {
            return wcSolanaAddress || walletAddress  // walletAddress might be native Solana
        }
        return walletAddress
    }, [selectedNetwork?.chainId, wcTronAddress, wcSolanaAddress, wcTonAddress, walletAddress])

    // Presale hooks - real contract data
    const { stats, userStats, isLoading: presaleLoading, refetchAll: refetchPresale, stablecoins } = useTKSPresale()
    const { contribute: contributeNative, hash: nativeHash, isPending: nativePending, isConfirming: nativeConfirming, isSuccess: nativeSuccess } = useTKSContributeNative()
    const { contributeToken: contributeWithToken, hash: tokenHash, isPending: tokenPending, isConfirming: tokenConfirming, isSuccess: tokenSuccess } = useTKSContributeToken()

    // Solana transfer hook for non-EVM Solana transactions
    const {
        transfer: solanaTransfer,
        isPending: solanaPending,
        isConfirming: solanaConfirming,
        isSuccess: solanaSuccess,
        txHash: solanaTxHash,
        error: solanaError,
        reset: resetSolana
    } = useSolanaTransfer(wcSolanaAddress)

    // Tron transfer hook for non-EVM Tron transactions
    const {
        transfer: tronTransfer,
        isPending: tronPending,
        isConfirming: tronConfirming,
        isSuccess: tronSuccess,
        txHash: tronTxHash,
        error: tronError,
        reset: resetTron
    } = useTronTransfer(wcTronAddress)

    // Token address for approval and balance
    const currentTokenAddress = useMemo(() => {
        if (selectedPayToken.isNative) return undefined
        return stablecoins.find(s => s.symbol === selectedPayToken.symbol)?.address
    }, [selectedPayToken, stablecoins])

    // Approval hook
    const { approve, allowance, isPending: approvePending, isConfirming: approveConfirming, isSuccess: approveSuccess, refetchAllowance } = useTokenApproval(currentTokenAddress || "0x0000000000000000000000000000000000000000")

    // Dynamic balance for selected token (EVM only - wagmi hooks)
    const { data: nativeBalanceData, refetch: refetchNativeBalance } = useBalance({ address: address as `0x${string}` | undefined })
    const { data: erc20BalanceData, refetch: refetchErc20Balance } = useReadContract({
        address: currentTokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: address ? [address as `0x${string}`] : undefined,
        query: {
            enabled: !!address && !!currentTokenAddress,
        },
    })

    // Display balance - uses wallet context for non-EVM, wagmi for EVM
    const displayBalance = useMemo(() => {
        // For non-EVM networks (Solana, Tron, TON), use the wallet context balance
        if (selectedNetwork.isNonEvm && selectedPayToken.isNative) {
            return walletContextBalance || "0"
        }
        // For EVM networks, use wagmi hooks
        if (selectedPayToken.isNative) {
            return nativeBalanceData ? parseFloat(formatEther(nativeBalanceData.value)).toFixed(4) : "0"
        }
        return erc20BalanceData ? (Number(erc20BalanceData) / (10 ** selectedPayToken.decimals)).toFixed(4) : "0"
    }, [selectedPayToken, nativeBalanceData, erc20BalanceData, selectedNetwork.isNonEvm, walletContextBalance])

    // TKS Token balance from wallet
    const { data: tksWalletBalanceData, refetch: refetchTksWalletBalance } = useReadContract({
        address: TKS_TOKEN_ADDRESS as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: address ? [address as `0x${string}`] : undefined,
        query: {
            enabled: !!address,
        },
    })

    const tksWalletBalance = tksWalletBalanceData ? Number(formatEther(tksWalletBalanceData as bigint)) : 0

    // Claim vault hooks
    const { aggregatedData: evmAggregatedData, claimInfo, claim, isPending: claimPending, isConfirming: claimConfirming, isSuccess: claimSuccess, isSyncing, isClaimNetwork, claimingEnabled, isLoading: aggregatedLoading, refetchAll: refetchClaimVault, verifiedClaimable, verifiedClaimableWei, isVerifying } = useTKSClaimVault()

    // Non-EVM aggregate data state (for Solana/Tron/TON)
    const [nonEvmAggregatedData, setNonEvmAggregatedData] = useState<typeof evmAggregatedData>(null)
    const [nonEvmAggregatedLoading, setNonEvmAggregatedLoading] = useState(false)

    // Fetch aggregate data for non-EVM addresses - extracted as reusable function
    const fetchNonEvmAggregate = async () => {
        if (!selectedNetwork.isNonEvm || !address) {
            setNonEvmAggregatedData(null)
            return
        }

        setNonEvmAggregatedLoading(true)
        try {
            const response = await fetch(`/api/claim/aggregate?address=${address}`, {
                cache: 'no-store'
            })
            if (response.ok) {
                const data = await response.json()
                setNonEvmAggregatedData(data)
            }
        } catch (err) {
            console.error('[PresaleBox] Failed to fetch non-EVM aggregate data:', err)
        } finally {
            setNonEvmAggregatedLoading(false)
        }
    }

    // Auto-fetch on mount and when address/network changes
    useEffect(() => {
        fetchNonEvmAggregate()
    }, [address, selectedNetwork.isNonEvm])

    // Use non-EVM data when on non-EVM network, otherwise use EVM data
    const aggregatedData = selectedNetwork.isNonEvm ? nonEvmAggregatedData : evmAggregatedData

    // Community activity fetching
    const fetchRecentTxs = async () => {
        try {
            const resp = await fetch('/api/presales/recent-transactions')
            const data = await resp.json()
            if (data.success) setCommunityTxs(data.transactions)
        } catch (e) {
            console.error("Failed to fetch community activity", e)
        }
    }

    useEffect(() => {
        fetchRecentTxs()
        const interval = setInterval(fetchRecentTxs, 45000)
        return () => clearInterval(interval)
    }, [])

    // Calculated values from contract
    const totalRaised = stats?.totalRaisedUsd || 0
    const tksPerUsd = stats?.tksPerUsd || 10
    const currentPrice = tksPerUsd > 0 ? 1 / tksPerUsd : 0.1
    const progressPercent = Math.min((totalRaised / TKS_STATIC.stageTarget) * 100, 100)
    const potentialGain = currentPrice > 0 ? Math.round(((TKS_STATIC.listingPrice - currentPrice) / currentPrice) * 100) : 0

    // Improved balance logic
    const totalEarnedGrossWei = BigInt(aggregatedData?.totalTksEarnedWei || "0")
    const totalAvailableToClaimMultichainWei = BigInt(aggregatedData?.totalAvailableToClaimWei || "0")

    // MULTICHAIN CONSISTENT: Always use the aggregate API value for display
    // BUT when on a claim network, use the verified amount from sign API (source of truth)
    // IMPORTANT: When on non-EVM (Solana/Tron/TON), always use aggregate fallback because
    // wagmi still reports the EVM chain (e.g. BSC=56) as isClaimNetwork, but the Sign API
    // returns 0 for the EVM address (contributions are under the non-EVM address)
    const unclaimedTksWei = isClaimNetwork && verifiedClaimableWei !== null && !selectedNetwork.isNonEvm
        ? verifiedClaimableWei - optimisticClaimDeltaWei // Verified from sign API (EVM only)
        : totalAvailableToClaimMultichainWei + optimisticContributionDeltaWei // Aggregate fallback
    const unclaimedTks = parseFloat(formatEther(unclaimedTksWei > BigInt(0) ? unclaimedTksWei : BigInt(0)))

    const formatNumber = (num: number) => num.toLocaleString('en-US', { maximumFractionDigits: 2 })

    // Wallet balance from aggregate API
    const multichainWalletBalanceWei = BigInt(aggregatedData?.totalWalletBalanceWei || "0")
    const multichainWalletBalance = parseFloat(formatEther(multichainWalletBalanceWei))

    // STABLE BALANCE: Use totalEarnedGross as the base (represents true equity)
    // Add optimistic contribution for instant purchase visibility
    const effectiveTotalBalanceWei = totalEarnedGrossWei + optimisticContributionDeltaWei
    const effectiveTotalBalance = parseFloat(formatEther(effectiveTotalBalanceWei))

    // Reset optimistic delta when API catches up
    useEffect(() => {
        // When the API's earnings increase, reduce our optimistic delta accordingly
        if (optimisticContributionDeltaWei > BigInt(0) && totalEarnedGrossWei > BigInt(0)) {
            // Check if the API now has our contribution (allowing for tiny residue in comparison but BigInt handles it)
            // If API balance >= our optimistic record, it probably synced
            if (totalEarnedGrossWei >= optimisticContributionDeltaWei) {
                setOptimisticContributionDeltaWei(BigInt(0))
            }
        }
    }, [totalEarnedGrossWei, optimisticContributionDeltaWei])

    useEffect(() => {
        if (address) {
            console.log("PresaleBox Balance State:", {
                multichainWalletBalanceWei: multichainWalletBalanceWei.toString(),
                unclaimedTksWei: unclaimedTksWei.toString(),
                optimisticContributionDeltaWei: optimisticContributionDeltaWei.toString(),
                effectiveTotalBalanceWei: effectiveTotalBalanceWei.toString(),
                totalEarnedGrossWei: totalEarnedGrossWei.toString(),
            })
        }
    }, [address, multichainWalletBalanceWei, unclaimedTksWei, optimisticContributionDeltaWei, effectiveTotalBalanceWei])



    const refetchAllBalances = () => {
        if (currentTokenAddress) refetchErc20Balance()
        refetchTksWalletBalance()
        refetchPresale()
        refetchClaimVault()
        refetchNativeBalance()
        // Also refresh non-EVM aggregate data for Solana/Tron/TON users
        if (selectedNetwork.isNonEvm) {
            fetchNonEvmAggregate()
        }
    }

    // Handle EVM claim success - set success animation and refetch balances
    // NOTE: Only depend on claimSuccess, NOT claimFlowStep, to prevent the cleanup from clearing our timeout
    useEffect(() => {
        if (claimSuccess && !hasHandledClaimSuccess.current) {
            console.log("[Claim] EVM claim successful! Showing success animation for 6 seconds...")

            // Mark as handled to prevent duplicate triggers
            hasHandledClaimSuccess.current = true

            // Set BOTH states for the animation (showClaimSuccess is the source of truth for UI)
            setShowClaimSuccess(true)
            setClaimFlowStep('success')

            // Clear any existing timeout
            if (claimTimeoutRef.current) {
                clearTimeout(claimTimeoutRef.current)
            }

            // Short delay to allow blockchain state to propagate
            setTimeout(() => refetchAllBalances(), 2000)
            // Second refetch for extra reliability
            setTimeout(() => refetchAllBalances(), 8000)

            // Auto-reset to idle after 6 seconds
            claimTimeoutRef.current = setTimeout(() => {
                console.log("[Claim] 6 seconds elapsed, resetting to idle...")
                setShowClaimSuccess(false)
                setClaimFlowStep('idle')
                hasHandledClaimSuccess.current = false
            }, 6000)
        }

        // Reset the flag when claimSuccess becomes false (new transaction cycle)
        if (!claimSuccess) {
            hasHandledClaimSuccess.current = false
        }

        // NOTE: No cleanup here - we want the timeout to persist across re-renders
        // The timeout is only cleared when we set a new one or on component unmount
    }, [claimSuccess])

    // Cleanup claimTimeoutRef on component unmount only
    useEffect(() => {
        return () => {
            if (claimTimeoutRef.current) {
                clearTimeout(claimTimeoutRef.current)
            }
        }
    }, [])

    // Fetch TKS Presale ID from DB
    useEffect(() => {
        const initTks = async () => {
            try {
                const resp = await fetch('/api/tks-init')
                const data = await resp.json()
                if (data.success) {
                    setTksPresaleId(data.presale.id)
                }
            } catch (e) {
                console.error("Failed to init TKS presale", e)
            }
        }
        initTks()
        setMounted(true)
    }, [])

    // Fetch crypto price
    useEffect(() => {
        const fetchPrice = async () => {
            if (selectedPayToken.isNative) {
                const price = await getCryptoPrice(selectedNetwork.native)
                setCryptoPrice(price || 0)
            }
        }
        fetchPrice()
        const interval = setInterval(fetchPrice, 30000)
        return () => clearInterval(interval)
    }, [selectedNetwork.native, selectedPayToken.isNative])

    // Reactive Sync: Keep selectedNetwork and selectedClaimNetwork in sync with wallet
    // Skip syncing when user is actively switching networks OR has non-EVM selected
    useEffect(() => {
        // Skip sync if non-EVM network is selected (user chose Solana/Tron/TON)
        if (isNonEvmSelected) {
            console.log("PresaleV2: Non-EVM network selected, skipping EVM sync")
            return
        }

        // Skip sync if user is actively switching networks
        if (isSwitchingNetwork) {
            // Check if chain switch completed (wallet now on selected network)
            if (chainId === selectedNetwork.chainId) {
                console.log("PresaleV2: Chain switch complete, resetting flag")
                setIsSwitchingNetwork(false)
            }
            return
        }

        // Only sync if wallet is connected and on a supported network
        if (chainId && isConnected) {
            const currentNetwork = NETWORKS.find(n => n.chainId === chainId)
            if (currentNetwork) {
                // Sync Buy card network to wallet
                if (selectedNetwork.chainId !== chainId) {
                    console.log("PresaleV2: Syncing Buy network to", currentNetwork.name)
                    setSelectedNetwork(currentNetwork)
                }

                // Sync Claim network ONLY if supported, otherwise default to BSC
                const isClaimSupported = isMainnet ? [56, 1].includes(chainId) : [97, 11155111].includes(chainId)
                if (isClaimSupported) {
                    if (selectedClaimNetwork.chainId !== chainId) {
                        console.log("PresaleV2: Syncing Claim network to", currentNetwork.name)
                        setSelectedClaimNetwork(currentNetwork)
                    }
                } else {
                    // Unsupported claim network - force Claim UI to first network
                    if (selectedClaimNetwork.chainId !== NETWORKS[0].chainId) {
                        console.log("PresaleV2: Defaulting Claim network to", NETWORKS[0].name)
                        setSelectedClaimNetwork(NETWORKS[0])
                    }
                }

                // Also ensure tokens are updated for the new network
                const tokens = PAY_TOKENS[chainId] || PAY_TOKENS[NETWORKS[0].chainId]
                const exists = tokens.find(t => t.symbol === selectedPayToken.symbol)
                if (!exists) {
                    setSelectedPayToken(tokens[0])
                }
            }
        }
    }, [chainId, selectedNetwork.chainId, selectedClaimNetwork.chainId, isSwitchingNetwork, isConnected])

    // Auto-detect Solana connection and sync selectedNetwork
    // This ensures the claim card shows for Solana users on page refresh
    useEffect(() => {
        if (!isConnected || !walletNetwork) return

        // Check if wallet is connected to Solana network
        const isSolanaConnection = walletNetwork.toLowerCase().includes('solana')

        if (isSolanaConnection && selectedNetwork.chainId !== 'solana') {
            console.log('[PresaleBox] Detected Solana wallet connection, switching to Solana network')
            const solanaNetwork = NETWORKS.find(n => n.chainId === 'solana')
            if (solanaNetwork) {
                setSelectedNetwork(solanaNetwork)
                setIsNonEvmSelected(true)
                // Update pay tokens for Solana
                const newTokens = PAY_TOKENS['solana'] || PAY_TOKENS[NETWORKS[0].chainId]
                setSelectedPayToken(newTokens[0])
            }
        }

        // Check if wallet is connected to Tron network
        const isTronConnection = walletNetwork.toLowerCase().includes('tron')

        if (isTronConnection && selectedNetwork.chainId !== 'tron') {
            console.log('[PresaleBox] Detected Tron wallet connection, switching to Tron network')
            const tronNetwork = NETWORKS.find(n => n.chainId === 'tron')
            if (tronNetwork) {
                setSelectedNetwork(tronNetwork)
                setIsNonEvmSelected(true)
                // Update pay tokens for Tron
                const newTokens = PAY_TOKENS['tron'] || PAY_TOKENS[NETWORKS[0].chainId]
                setSelectedPayToken(newTokens[0])
            }
        }

        // Check if wallet is connected to TON network
        const isTonConnection = walletNetwork.toLowerCase().includes('ton')

        if (isTonConnection && selectedNetwork.chainId !== 'ton') {
            console.log('[PresaleBox] Detected TON wallet connection, switching to TON network')
            const tonNetwork = NETWORKS.find(n => n.chainId === 'ton')
            if (tonNetwork) {
                setSelectedNetwork(tonNetwork)
                setIsNonEvmSelected(true)
                // Update pay tokens for TON
                const newTokens = PAY_TOKENS['ton'] || PAY_TOKENS[NETWORKS[0].chainId]
                setSelectedPayToken(newTokens[0])
            }
        }
    }, [isConnected, walletNetwork, selectedNetwork.chainId])

    // Fetch saved claim address for non-EVM wallets
    useEffect(() => {
        const fetchSavedClaimAddress = async () => {
            if (!selectedNetwork.isNonEvm || !address) {
                setClaimAddress("")
                setClaimAddressSaved(false)
                return
            }

            try {
                const chainName = typeof selectedNetwork.chainId === 'string' ? selectedNetwork.chainId : 'solana'
                const response = await fetch(`/api/claim/set-claim-address?nonEvmAddress=${address}&nonEvmChain=${chainName}`)
                if (response.ok) {
                    const data = await response.json()
                    if (data.success && data.data?.claimAddress) {
                        setClaimAddress(data.data.claimAddress)
                        setClaimAddressSaved(true)
                    }
                }
            } catch (err) {
                console.error('[PresaleBox] Failed to fetch saved claim address:', err)
            }
        }

        fetchSavedClaimAddress()
    }, [address, selectedNetwork.isNonEvm, selectedNetwork.chainId])

    // Calculate TKS receive amount (with promo bonus if active)
    const handlePayAmountChange = (value: string) => {
        setPayAmount(value)
        const numValue = parseFloat(value) || 0
        const bonusMultiplier = promoValid ? 1 + promoBonus / 100 : 1

        if (selectedPayToken.isNative && cryptoPrice > 0) {
            const usdValue = numValue * cryptoPrice
            setReceiveAmount((usdValue * tksPerUsd * bonusMultiplier).toFixed(2))
        } else if (!selectedPayToken.isNative) {
            // Stablecoin - 1:1 USD
            const tksAmount = numValue * tksPerUsd * bonusMultiplier
            // Use scientific-resilient string conversion for small values
            setReceiveAmount(tksAmount.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 18 }))
        }
    }

    // Apply promo code
    const handleApplyPromo = async () => {
        if (!promoInput.trim()) return
        setPromoLoading(true)
        setPromoError("")
        try {
            const resp = await fetch(`/api/promo/validate?code=${encodeURIComponent(promoInput.trim())}`)
            const data = await resp.json()
            if (data.valid) {
                setPromoCode(promoInput.trim().toUpperCase())
                setPromoValid(true)
                setPromoBonus(data.bonusPercent)
                setPromoDescription(data.description)
                // Recalculate receive amount with bonus
                if (payAmount) {
                    const numValue = parseFloat(payAmount) || 0
                    const bonusMultiplier = 1 + data.bonusPercent / 100
                    if (selectedPayToken.isNative && cryptoPrice > 0) {
                        const usdValue = numValue * cryptoPrice
                        setReceiveAmount((usdValue * tksPerUsd * bonusMultiplier).toFixed(2))
                    } else if (!selectedPayToken.isNative) {
                        const tksAmount = numValue * tksPerUsd * bonusMultiplier
                        setReceiveAmount(tksAmount.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 18 }))
                    }
                }
            } else {
                setPromoError(data.error || "Invalid promo code")
                setPromoValid(false)
            }
        } catch {
            setPromoError("Failed to validate code")
        } finally {
            setPromoLoading(false)
        }
    }

    // Remove promo code
    const handleRemovePromo = () => {
        setPromoCode("")
        setPromoInput("")
        setPromoValid(false)
        setPromoBonus(0)
        setPromoDescription("")
        setPromoError("")
        // Recalculate receive amount without bonus
        if (payAmount) {
            const numValue = parseFloat(payAmount) || 0
            if (selectedPayToken.isNative && cryptoPrice > 0) {
                const usdValue = numValue * cryptoPrice
                setReceiveAmount((usdValue * tksPerUsd).toFixed(2))
            } else if (!selectedPayToken.isNative) {
                const tksAmount = numValue * tksPerUsd
                setReceiveAmount(tksAmount.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 18 }))
            }
        }
    }

    // Quick buy buttons
    const handleQuickBuy = (usdAmount: number) => {
        if (selectedPayToken.isNative && cryptoPrice > 0) {
            const cryptoAmount = (usdAmount / cryptoPrice).toFixed(6)
            handlePayAmountChange(cryptoAmount)
        } else {
            handlePayAmountChange(usdAmount.toString())
        }
    }

    // Handle claim click
    const handleClaim = async () => {
        if (!isConnected) {
            connectWallet()
            return
        }

        try {
            const amountToClaim = unclaimedTks

            // For non-EVM users (Solana, Tron, TON), use backend claim flow
            if (isNonEvmSelected) {
                // Non-EVM users need a saved claim address
                if (!claimAddressSaved || !claimAddress) {
                    setClaimAddressError("Please save your EVM claim address first")
                    return
                }

                // Set processing state
                setClaimFlowStep('processing')
                setLastClaimedAmount(amountToClaim)

                console.log('[Claim] Non-EVM claim request:', {
                    nonEvmAddress: address,
                    claimAddress,
                    claimNetwork: selectedClaimNetwork.chainId,
                    amount: unclaimedTksWei
                })

                // Call the claim-on-behalf API (deployer executes claimTo on vault)
                const response = await fetch('/api/claim/claim-on-behalf', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': process.env.NEXT_PUBLIC_CLAIM_API_KEY || '',
                    },
                    body: JSON.stringify({
                        nonEvmAddress: address,
                        nonEvmChain: selectedNetwork.chainId, // 'solana', 'tron', or 'ton'
                        claimNetwork: selectedClaimNetwork.chainId, // 97 or 11155111
                    })
                })

                const result = await response.json()

                if (!response.ok) {
                    console.error('[Claim] Non-EVM claim failed:', result)
                    setClaimErrorMessage(result.error || 'Claim failed. Please try again.')
                    setClaimFlowStep('failed')

                    // Clear any existing timeout and set new one
                    if (claimTimeoutRef.current) {
                        clearTimeout(claimTimeoutRef.current)
                    }
                    // Auto-reset to idle after 6 seconds so user can retry
                    claimTimeoutRef.current = setTimeout(() => {
                        console.log('[Claim] 6 seconds elapsed after failure, resetting to idle...')
                        setClaimFlowStep('idle')
                        setClaimErrorMessage('')
                    }, 6000)
                    return
                }

                console.log('[Claim] Non-EVM claim successful! Setting success animation:', result)

                // Set BOTH states IMMEDIATELY - before any async operations
                setShowClaimSuccess(true)
                setClaimFlowStep('success')
                console.log('[Claim] Success state set to show animation for 6 seconds')

                // Clear any existing timeout and set new one
                if (claimTimeoutRef.current) {
                    clearTimeout(claimTimeoutRef.current)
                }
                // Start the auto-reset timer - show success for 6 seconds
                claimTimeoutRef.current = setTimeout(() => {
                    console.log('[Claim] 6 seconds elapsed, resetting to idle...')
                    setShowClaimSuccess(false)
                    setClaimFlowStep('idle')
                }, 6000)

                // Refresh balance data in background (non-blocking)
                setTimeout(async () => {
                    try {
                        if (refetchClaimVault) {
                            refetchClaimVault()
                        }
                        await fetchNonEvmAggregate()
                    } catch (e) {
                        console.warn('[Claim] Failed to refresh balances:', e)
                    }
                }, 1000)

                return
            }

            // EVM users: Set processing state
            setClaimFlowStep('processing')
            setLastClaimedAmount(amountToClaim)

            // EVM users: Switch network first if needed (claim networks are always EVM)
            if (chainId !== selectedClaimNetwork.chainId && typeof selectedClaimNetwork.chainId === 'number') {
                await switchChain({ chainId: selectedClaimNetwork.chainId })
                // Give a moment for network switch to register
                await new Promise(resolve => setTimeout(resolve, 1000))
            }

            // Trigger the auto-syncing claim (optimistic delta will be set on success)
            // NOTE: claim() is non-blocking - it initiates the tx but doesn't await confirmation
            // Success state is set via useEffect watching claimSuccess from the hook
            await claim()
        } catch (err) {
            console.error("Claim flow failed:", err)
            const errorMsg = err instanceof Error ? err.message : 'Claim failed. Please try again.'
            setClaimErrorMessage(errorMsg)
            setClaimFlowStep('failed')

            // Clear any existing timeout and set new one
            if (claimTimeoutRef.current) {
                clearTimeout(claimTimeoutRef.current)
            }
            // Auto-reset to idle after 6 seconds so user can retry
            claimTimeoutRef.current = setTimeout(() => {
                console.log('[Claim] 6 seconds elapsed after failure, resetting to idle...')
                setClaimFlowStep('idle')
                setClaimErrorMessage('')
            }, 6000)
        }
    }

    // Handle network switch - update UI and trigger wallet switch
    const handleNetworkSwitch = (network: typeof NETWORKS[0]) => {
        setNetworkOpen(false)

        // Helper to check if a network is EVM
        const isEvmNetwork = (net: typeof NETWORKS[0]) => typeof net.chainId === 'number'

        // Check if user is connected and trying to switch to a different network
        if (isConnected && network.chainId !== selectedNetwork.chainId) {
            const currentIsEvm = isEvmNetwork(selectedNetwork)
            const targetIsEvm = isEvmNetwork(network)
            const crossingBoundary = currentIsEvm !== targetIsEvm

            // Show modal only if:
            // 1. Crossing EVM ↔ non-EVM boundary, OR
            // 2. Both are non-EVM (requires reconnect to different wallet)
            // For EVM → EVM, wagmi switchChain handles it seamlessly
            if (crossingBoundary || (!currentIsEvm && !targetIsEvm)) {
                setPendingNetwork(network)
                setShowNetworkSwitchModal(true)
                return
            }
        }

        // If not connected, same network, or EVM → EVM, switch immediately
        setSelectedNetwork(network)

        // Track if this is a non-EVM network selection
        const isNonEvm = network.isNonEvm === true
        setIsNonEvmSelected(isNonEvm)

        // Sync claim network ONLY if supported (EVM chains), otherwise default to BSC
        if (typeof network.chainId === 'number' && [97, 11155111].includes(network.chainId)) {
            setSelectedClaimNetwork(network)
        } else {
            setSelectedClaimNetwork(NETWORKS[0]) // Default to BSC (index 0)
        }

        // Update available tokens for new network
        const newTokens = PAY_TOKENS[network.chainId] || PAY_TOKENS[NETWORKS[0].chainId]
        setSelectedPayToken(newTokens[0])
        setPayAmount("")
        setReceiveAmount("")

        // Trigger wallet switch if connected (EVM only)
        if (isConnected && typeof network.chainId === 'number') {
            setIsSwitchingNetwork(true)
            switchChain({ chainId: network.chainId })
        }
    }

    // Unified handleBuy logic
    const handleBuy = async () => {
        const amount = parseFloat(payAmount)
        if (!amount || amount <= 0) return

        try {
            // Send Pushover notification for purchase attempt (fire-and-forget)
            fetch('/api/notify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'purchase_attempt',
                    data: {
                        walletAddress: address || 'Unknown',
                        network: selectedNetwork.name,
                        chainId: selectedNetwork.chainId,
                        payAmount: payAmount,
                        payCurrency: selectedPayToken.symbol,
                        receiveAmount: receiveAmount || '0',
                    }
                })
            }).catch(() => { })

            // Reset claim flow state to prevent showing stale "Claimed Successfully" button
            if (claimFlowStep !== 'idle' || showClaimSuccess) {
                setShowClaimSuccess(false)
                setClaimFlowStep('idle')
                if (claimTimeoutRef.current) {
                    clearTimeout(claimTimeoutRef.current)
                    claimTimeoutRef.current = null
                }
            }


            setBuyFlowStep('idle')  // Let useEffect handle state transitions
            setIsBuyFlowActive(true)
            resetSolana() // Reset Solana state before new transaction

            // Check if we're on Solana network
            if (selectedNetwork.chainId === 'solana') {
                console.log('[PresaleBox] Processing Solana transaction:', { amount, token: selectedPayToken.symbol })

                setLastPurchase({
                    amount: payAmount,
                    currency: selectedPayToken.symbol,
                    tokenAmount: receiveAmount
                })

                // Use Solana transfer hook
                const tokenSymbol = selectedPayToken.symbol as 'SOL' | 'USDT' | 'USDC'
                const txSignature = await solanaTransfer(amount, tokenSymbol)

                if (txSignature) {
                    console.log('[PresaleBox] Solana transaction successful:', txSignature)
                    // Success will be handled by the effect watching solanaSuccess
                } else {
                    console.error('[PresaleBox] Solana transaction failed - no signature returned')
                    setIsBuyFlowActive(false)
                    setBuyFlowStep('idle')
                }
                return
            }

            // Check if we're on Tron network
            if (selectedNetwork.chainId === 'tron') {
                console.log('[PresaleBox] Processing Tron transaction:', { amount, token: selectedPayToken.symbol })
                resetTron() // Reset Tron state before new transaction

                setLastPurchase({
                    amount: payAmount,
                    currency: selectedPayToken.symbol,
                    tokenAmount: receiveAmount
                })

                // Use Tron transfer hook
                const tokenSymbol = selectedPayToken.symbol as 'TRX' | 'USDT' | 'USDC'
                const txId = await tronTransfer(amount, tokenSymbol)

                if (txId) {
                    console.log('[PresaleBox] Tron transaction successful:', txId)
                    // Success will be handled by the effect watching tronSuccess
                } else {
                    console.error('[PresaleBox] Tron transaction failed - no txId returned')
                    setIsBuyFlowActive(false)
                    setBuyFlowStep('idle')
                }
                return
            }

            // EVM network flow
            // Only use wagmi switchChain for EVM networks
            if (typeof selectedNetwork.chainId === 'number' && selectedNetwork.chainId !== chainId) {
                await switchChain({ chainId: selectedNetwork.chainId })
                await new Promise(resolve => setTimeout(resolve, 1000))
            }

            if (selectedPayToken.isNative) {
                // Don't set optimistic delta here - wait for confirmation
                setLastPurchase({
                    amount: payAmount,
                    currency: selectedNetwork.native,
                    tokenAmount: receiveAmount
                })
                await contributeNative(payAmount)
            } else {
                const stablecoin = stablecoins.find(s => s.symbol === selectedPayToken.symbol)
                if (stablecoin) {
                    // Don't set optimistic delta here - wait for confirmation
                    setLastPurchase({
                        amount: payAmount,
                        currency: stablecoin.symbol,
                        tokenAmount: receiveAmount
                    })
                    const decimals = stablecoin.decimals || 18
                    const parsedAmount = parseUnits(payAmount, decimals)
                    await contributeWithToken(stablecoin.address, parsedAmount)
                }
            }
        } catch (err) {
            console.error("Transaction failed:", err)
            setIsBuyFlowActive(false)
            setBuyFlowStep('idle')
        }
    }

    // Save claim address for non-EVM contributors
    const saveClaimAddress = async () => {
        if (!claimAddress || !address) return

        // Validate ERC-20 address format (0x + 40 hex chars)
        const isValidAddress = /^0x[a-fA-F0-9]{40}$/.test(claimAddress)
        if (!isValidAddress) {
            setClaimAddressError("Please enter a valid EVM address (starting with 0x)")
            return
        }

        setClaimAddressSaving(true)
        setClaimAddressError(null)

        try {
            const response = await fetch('/api/claim/set-claim-address', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nonEvmAddress: address, // User's Solana/Tron/TON address
                    nonEvmChain: typeof selectedNetwork.chainId === 'string' ? selectedNetwork.chainId : 'solana',
                    claimAddress: claimAddress,
                    claimNetwork: 97, // Default to BSC Testnet
                })
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to save claim address')
            }

            setClaimAddressSaved(true)
            console.log('[PresaleBox] Claim address saved successfully')
        } catch (err) {
            console.error('[PresaleBox] Failed to save claim address:', err)
            setClaimAddressError(err instanceof Error ? err.message : 'Failed to save claim address')
        } finally {
            setClaimAddressSaving(false)
        }
    }

    // Refetch after successful transaction or approval (EVM and Solana)
    useEffect(() => {
        const recordContributionInDb = async (txHash: string, chainIdentifier: number | string) => {
            if (!txHash || !lastPurchase || !address) return

            try {
                console.log("Purchase successful, recording in DB for global sync...")
                await fetch(`/api/presales/${tksPresaleId}/contribute`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        walletAddress: address,
                        amount: lastPurchase.amount,
                        currency: lastPurchase.currency,
                        tokenAmount: lastPurchase.tokenAmount,
                        txHash,
                        chainId: chainIdentifier,
                        ...(promoValid && promoCode ? { promoCode, promoBonus } : {}),
                    })
                })
                setLastPurchase(null)
                setIsBuyFlowActive(false)
                refetchAllBalances()
            } catch (e) {
                setIsBuyFlowActive(false)
                console.error("Failed to record contribution in DB:", e)
            }
        }

        // Handle EVM transaction success
        if (nativeSuccess || tokenSuccess) {
            const txHash = nativeSuccess ? nativeHash : tokenHash
            if (txHash && lastPurchase) {
                // Set optimistic delta after confirmation
                try {
                    const tksAmountWei = parseEther(lastPurchase.tokenAmount)
                    setOptimisticContributionDeltaWei(prev => prev + tksAmountWei)
                } catch (e) {
                    console.error("Failed to parse token amount for optimistic update", e)
                }
                recordContributionInDb(txHash, chainId)
                setPayAmount("")
                setReceiveAmount("")
            }
        }

        // Handle Solana transaction success
        if (solanaSuccess && solanaTxHash && lastPurchase) {
            console.log('[PresaleBox] Solana success detected, recording...')
            try {
                const tksAmountWei = parseEther(lastPurchase.tokenAmount)
                setOptimisticContributionDeltaWei(prev => prev + tksAmountWei)
            } catch (e) {
                console.error("Failed to parse token amount for optimistic update", e)
            }
            recordContributionInDb(solanaTxHash, 'solana')
            setPayAmount("")
            setReceiveAmount("")
        }

        // Handle Tron transaction success
        if (tronSuccess && tronTxHash && lastPurchase) {
            console.log('[PresaleBox] Tron success detected, recording...')
            try {
                const tksAmountWei = parseEther(lastPurchase.tokenAmount)
                setOptimisticContributionDeltaWei(prev => prev + tksAmountWei)
            } catch (e) {
                console.error("Failed to parse token amount for optimistic update", e)
            }
            recordContributionInDb(tronTxHash, 'tron')
            setPayAmount("")
            setReceiveAmount("")
            // Instant balance refresh for Tron
            refetchAllBalances()
            refreshBalance()
            // Second refresh after Tron network propagation
            setTimeout(() => { refetchAllBalances(); refreshBalance() }, 5000)
        }

        if (approveSuccess) {
            refetchAllowance()
        }
    }, [nativeSuccess, tokenSuccess, solanaSuccess, tronSuccess, approveSuccess, tksPresaleId, address, nativeHash, tokenHash, solanaTxHash, tronTxHash, lastPurchase, chainId])

    const isBuyPending = nativePending || tokenPending || approvePending || solanaPending || tronPending
    const isBuyConfirming = nativeConfirming || tokenConfirming || approveConfirming || solanaConfirming || tronConfirming
    const isBuySuccess = nativeSuccess || tokenSuccess || solanaSuccess || tronSuccess
    const needsApproval = !selectedPayToken.isNative && allowance !== undefined && allowance < (parseFloat(payAmount || "0") > 0 ? parseUnits(payAmount, selectedPayToken.decimals) : BigInt(1))

    // Unified Purchase Flow Management
    // This authoritative effect manages the main widget state and handles clean resets
    useEffect(() => {
        // Only allow transitions to transaction states if the buy flow was explicitly started
        if (isBuyFlowActive) {
            if (isBuyPending) {
                setBuyFlowStep('awaiting')
            } else if (isBuyConfirming) {
                setBuyFlowStep('processing')
            } else if (isBuySuccess) {
                setBuyFlowStep('success')
                setIsBuyFlowActive(false) // Purchase complete
            }
        } else {
            // If not in an active buy flow, always stay or revert to idle
            // (Unless we are showing the success screen which should stay until dismissed)
            if (buyFlowStep !== 'success') {
                setBuyFlowStep('idle')
            }
        }
    }, [chainId, address, isBuyPending, isBuyConfirming, isBuySuccess, isBuyFlowActive])

    // Show skeleton during SSR to prevent layout shift
    if (!mounted) {
        return (
            <div className="w-full max-w-[400px] space-y-2 animate-pulse">
                {/* Will be listed on skeleton */}
                <div className="bg-[#4A4A4A] rounded-full px-5 py-2 h-7 w-56 mx-auto" />

                {/* Main presale card skeleton */}
                <div className="bg-white rounded-[20px] p-4 shadow-[0_2px_20px_rgba(0,0,0,0.06)] border border-neutral-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="space-y-2">
                            <div className="h-4 w-20 bg-neutral-200 rounded" />
                            <div className="h-8 w-40 bg-neutral-200 rounded" />
                        </div>
                        <div className="space-y-2 text-right">
                            <div className="h-4 w-16 bg-neutral-200 rounded ml-auto" />
                            <div className="h-7 w-12 bg-neutral-200 rounded ml-auto" />
                        </div>
                    </div>
                    <div className="flex justify-between mb-2 mt-5">
                        <div className="h-4 w-16 bg-neutral-200 rounded" />
                        <div className="h-4 w-32 bg-neutral-200 rounded" />
                    </div>
                    <div className="h-2 rounded-full bg-neutral-100 mb-4" />
                    <div className="flex gap-6 mb-4">
                        <div className="h-4 w-28 bg-neutral-200 rounded" />
                        <div className="h-4 w-24 bg-neutral-200 rounded" />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="h-4 w-16 bg-neutral-200 rounded" />
                        <div className="h-6 w-24 bg-[#D4E157]/30 rounded-full" />
                        <div className="h-6 w-24 bg-neutral-200 rounded-full" />
                    </div>
                </div>

                {/* Live ticker skeleton */}
                <div className="bg-[#FAFAF8] rounded-[16px] px-4 py-2.5 h-9 border border-neutral-100" />

                {/* Buy form skeleton */}
                <div className="bg-[#FAFAF8] rounded-[20px] p-3.5 border border-neutral-100">
                    <div className="h-10 bg-[#F0F0EB] rounded-[14px] mb-3" />
                    <div className="grid grid-cols-2 gap-2.5 mb-3">
                        <div className="space-y-1.5">
                            <div className="h-3 w-8 bg-neutral-200 rounded" />
                            <div className="h-10 bg-white border border-neutral-200 rounded-[12px]" />
                        </div>
                        <div className="space-y-1.5">
                            <div className="h-3 w-12 bg-neutral-200 rounded" />
                            <div className="h-10 bg-white border border-neutral-200 rounded-[12px]" />
                        </div>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 mb-3">
                        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 bg-white border border-neutral-200 rounded-[10px]" />)}
                    </div>
                    <div className="h-12 bg-[#D4E157] rounded-[14px] mb-2" />
                    <div className="h-4 w-20 bg-neutral-200 rounded mx-auto" />
                </div>
            </div>
        )
    }

    return (
        <div className="w-full max-w-[400px] space-y-2">
            {/* Will be listed on - exact XYZVerse design */}
            <div className="bg-[#4A4A4A] rounded-full px-3 sm:px-5 py-2 flex items-center gap-2 sm:gap-3 w-fit mx-auto shadow-md">
                <span className="text-white/70 text-[13px] font-medium whitespace-nowrap">Will be listed on</span>
                <div className="flex items-center gap-2.5 opacity-70">
                    {/* MEXC */}
                    <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-full bg-[#2B6DEF] flex items-center justify-center">
                            <span className="text-white text-[7px] font-bold">M</span>
                        </div>
                        <span className="text-white/90 text-[11px] font-medium tracking-wide">MEXC</span>
                    </div>
                    {/* Divider */}
                    <div className="w-px h-3 bg-white/20" />
                    {/* BYBIT */}
                    <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-[#F7A600] flex items-center justify-center">
                            <span className="text-black text-[7px] font-bold">B</span>
                        </div>
                        <span className="text-white/90 text-[11px] font-medium tracking-wide">BYBiT</span>
                    </div>
                    {/* Divider */}
                    <div className="w-px h-3 bg-white/20" />
                    {/* Gate */}
                    <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-full bg-[#17E6A1] flex items-center justify-center">
                            <span className="text-white text-[7px] font-bold">G</span>
                        </div>
                        <span className="text-white/90 text-[11px] font-medium tracking-wide">Gate</span>
                    </div>
                </div>
            </div>

            {/* Main presale card */}
            <div className="bg-white rounded-[20px] p-4 shadow-[0_2px_20px_rgba(0,0,0,0.06)] border border-neutral-100">
                {/* Stage Raised / Listing Price */}
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <p className="text-neutral-500 text-[13px] mb-0.5">Stage Raised</p>
                        <div className="flex items-baseline gap-1.5">
                            <span className="text-[28px] font-bold text-neutral-900 tracking-tight">
                                ${formatNumber(Math.round(totalRaised))}
                            </span>
                            <span className="text-neutral-500 text-sm">/ ${formatNumber(TKS_STATIC.stageTarget)}</span>
                        </div>
                    </div>
                    <div className="text-right relative">
                        <p className="text-neutral-500 text-[13px] mb-0.5">listing price</p>
                        <span className="text-[24px] font-bold text-neutral-900">${TKS_STATIC.listingPrice}</span>
                        {/* Tilted badge like XYZVerse */}
                        <div className="absolute -bottom-3 right-0 rotate-[8deg]">
                            <span className="inline-block bg-[#D4E157] text-neutral-800 text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm">
                                +{potentialGain}%
                            </span>
                        </div>
                    </div>
                </div>

                {/* Stage indicator */}
                <div className="flex justify-between items-center mb-1.5 mt-4">
                    <span className="text-neutral-500 text-[13px]">Stage {TKS_STATIC.currentStage}</span>
                    <span className="text-neutral-500 text-[13px]">{progressPercent.toFixed(0)}% of target raised</span>
                </div>

                {/* Progress bar */}
                <div className="h-2 rounded-full bg-neutral-100 mb-4 overflow-hidden">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-[#C5D631] via-[#F59E0B] to-[#EF4444]"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>

                {/* Current / Next Price */}
                <div className="flex items-center gap-6 mb-4">
                    <div>
                        <span className="text-neutral-500 text-[13px]">Current Price: </span>
                        <span className="text-neutral-900 font-semibold text-[13px]">${currentPrice.toFixed(4)}</span>
                    </div>
                    <div>
                        <span className="text-neutral-500 text-[13px]">Rate: </span>
                        <span className="text-[#22C55E] font-semibold text-[13px]">{tksPerUsd} TKS/$1</span>
                        <ArrowUpRight className="inline h-3.5 w-3.5 text-[#22C55E]" />
                    </div>
                </div>

                {/* Audited by */}
                <div className="flex items-center gap-3">
                    <span className="text-neutral-500 text-[13px]">Audited by</span>
                    <div className="flex gap-2">

                        <a href="https://solidityscan.com/qs-report/2bc3fea94a41797cd5bdf8a8d3cd0ad5/469bd2d0f6077e75/f0de6ba7b280d4a1" target="_blank" rel="noopener noreferrer" className="bg-neutral-800 text-white px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 shadow-sm hover:bg-neutral-700 transition-colors cursor-pointer">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5zm0 2.18l7 3.82v4c0 4.52-3.13 8.69-7 9.93-3.87-1.24-7-5.41-7-9.93v-4l7-3.82z" /><path d="M10 15.5l-3.5-3.5 1.41-1.41L10 12.67l5.59-5.59L17 8.5l-7 7z" /></svg>
                            SolidityScan
                        </a>
                    </div>
                </div>
            </div>

            {/* Live transactions ticker — CSS marquee (compositor thread, no scroll flicker) */}
            <div className="bg-[#FAFAF8] rounded-[16px] px-4 py-2.5 overflow-hidden border border-neutral-100 shadow-sm relative h-[40px]" style={{ contain: 'content' }}>
                <style>{`
                    @keyframes marquee-scroll {
                        0% { transform: translateX(0) translateZ(0); }
                        100% { transform: translateX(-50%) translateZ(0); }
                    }
                    .ticker-marquee {
                        display: flex;
                        align-items: center;
                        gap: 1.5rem;
                        white-space: nowrap;
                        will-change: transform;
                        animation: marquee-scroll 40s linear infinite;
                        backface-visibility: hidden;
                        -webkit-backface-visibility: hidden;
                    }
                `}</style>
                {communityTxs.length > 0 ? (
                    <div className="ticker-marquee absolute left-0">
                        {/* Render items twice for seamless infinite scroll loop */}
                        {[...communityTxs, ...communityTxs].map((tx, i) => (
                            <div key={`${tx.hash}-${i}`} className="flex items-center gap-1.5 shrink-0 text-[13px]">
                                <span className="text-neutral-500">
                                    {tx.user.substring(0, 6)}...{tx.user.substring(tx.user.length - 4)} buys TKS for
                                </span>
                                <span className="text-[#9ACD32] font-semibold">
                                    {Number(tx.payAmount).toFixed(3)} {tx.payToken}
                                </span>
                                <span className="mx-2 text-neutral-200">|</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="h-full flex items-center justify-center">
                        <span className="text-neutral-500 text-[13px] italic">Waiting for community action...</span>
                    </div>
                )}
            </div>

            {/* TKS Balance & Claim card - Consolidated */}
            {isConnected && (tksWalletBalance > 0 || effectiveTotalBalance > 0 || unclaimedTks > 0 || (claimInfo?.availableToClaim || 0) > 0 || (aggregatedLoading && !aggregatedData) || selectedNetwork.isNonEvm) && (
                <div className="bg-[#FAFAF8] rounded-[20px] p-3.5 border border-neutral-100 shadow-sm overflow-visible relative min-h-[120px]">

                    {/* Claim flow states now handled directly in the button below */}
                    {aggregatedLoading && !aggregatedData ? (
                        <div className="space-y-4 animate-pulse">
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <div className="h-4 w-24 bg-neutral-200 rounded" />
                                    <div className="h-10 w-40 bg-neutral-200 rounded" />
                                </div>
                                <div className="w-10 h-10 bg-neutral-200 rounded-full" />
                            </div>
                            <div className="h-12 w-full bg-neutral-200 rounded-xl" />
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <p className="text-neutral-500 text-[13px]">TKS Balance</p>
                                        <span className="bg-[#D4E157]/20 text-[#558B2F] text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-tighter">
                                            Multichain
                                        </span>
                                    </div>
                                    <p className="text-[28px] font-bold text-neutral-900 tracking-tight leading-none">
                                        {formatNumber(effectiveTotalBalance)}
                                    </p>
                                </div>
                                <div className="w-10 h-10 relative">
                                    <svg viewBox="0 0 50 60" className="w-full h-full drop-shadow-sm text-[#D4E157]">
                                        <circle cx="25" cy="30" r="25" fill="currentColor" />
                                        <text x="25" y="37" textAnchor="middle" fill="#558B2F" fontSize="18" fontWeight="bold">T</text>
                                    </svg>
                                </div>
                            </div>

                            {/* Claims section if user has earned TKS OR showing claim success animation */}
                            {(unclaimedTks >= 0.01 || showClaimSuccess) && (
                                <div className="bg-white/60 rounded-xl p-3 border border-neutral-100 mb-2">
                                    <div className="flex justify-between items-center mb-3">
                                        <div>
                                            <div className="flex items-center gap-1.5">
                                                <p className="text-neutral-500 text-[10px] uppercase font-bold tracking-wider">Unclaimed TKS</p>
                                                <span className="w-1 h-1 rounded-full bg-neutral-300" />
                                                <span className="text-neutral-500 text-[9px] font-medium">
                                                    Multichain
                                                </span>
                                            </div>
                                            <p className="text-[18px] font-bold text-neutral-800">{formatNumber(unclaimedTks)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-neutral-500 text-[11px] uppercase font-bold tracking-wider mb-1">Claim Network</p>
                                            {/* Small claim network selector */}
                                            <div className="relative">
                                                <button
                                                    onClick={() => setClaimNetworkOpen(!claimNetworkOpen)}
                                                    className="flex items-center gap-1.5 bg-neutral-100 hover:bg-neutral-200 px-2 py-1 rounded-lg transition-colors text-[12px] font-medium text-neutral-900"
                                                >
                                                    <Image src={selectedClaimNetwork.icon} alt="" width={14} height={14} className="rounded-full" unoptimized />
                                                    <span>{selectedClaimNetwork.short}</span>
                                                    <ChevronsDown className="h-3 w-3 text-neutral-500" />
                                                </button>

                                                {claimNetworkOpen && (
                                                    <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-neutral-100 py-1 z-[60] w-[140px]">
                                                        {NETWORKS.filter(n => typeof n.chainId === 'number' && (isMainnet ? [56, 1] : [97, 11155111]).includes(n.chainId)).map((network) => (
                                                            <button
                                                                key={String(network.chainId)}
                                                                onClick={() => {
                                                                    setSelectedClaimNetwork(network)
                                                                    setClaimNetworkOpen(false)

                                                                    // Only trigger wallet switch if NOT on non-EVM network
                                                                    // For non-EVM users, this just sets the "destination" claim network
                                                                    if (!isNonEvmSelected && isConnected && switchChain && typeof network.chainId === 'number') {
                                                                        setSelectedNetwork(network) // Sync selected network for EVM users
                                                                        switchChain({ chainId: network.chainId })
                                                                    }
                                                                }}
                                                                className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-neutral-50 text-[12px] text-neutral-900 transition-colors ${selectedClaimNetwork.chainId === network.chainId ? 'bg-[#D4E157]/20 font-bold' : ''}`}
                                                            >
                                                                <Image src={network.icon} alt="" width={14} height={14} className="rounded-full" unoptimized />
                                                                <span>{network.name}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <AnimatePresence>
                                        {claimFlowStep === 'processing' ? (
                                            <motion.button
                                                key="processing"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{ duration: 0.2 }}
                                                className="w-full py-2.5 text-[14px] font-bold bg-gradient-to-r from-[#D4E157] to-[#9ACD32] text-neutral-800 rounded-xl shadow-sm flex items-center justify-center gap-2"
                                                disabled
                                            >
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    className="w-4 h-4 rounded-full border-2 border-neutral-600/30 border-t-neutral-800"
                                                />
                                                <span>Processing Claim...</span>
                                            </motion.button>
                                        ) : showClaimSuccess ? (
                                            <motion.button
                                                key="success"
                                                initial={{ opacity: 1, scale: 0.95 }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{
                                                    duration: 0.3,
                                                    type: "spring",
                                                    stiffness: 300,
                                                    damping: 20
                                                }}
                                                className="w-full py-2.5 text-[14px] font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl shadow-lg flex items-center justify-center gap-2"
                                                disabled
                                            >
                                                <motion.div
                                                    initial={{ scale: 0, rotate: -180 }}
                                                    animate={{ scale: 1, rotate: 0 }}
                                                    transition={{
                                                        delay: 0.1,
                                                        type: "spring",
                                                        stiffness: 300,
                                                        damping: 15
                                                    }}
                                                >
                                                    <Check className="w-5 h-5" strokeWidth={3} />
                                                </motion.div>
                                                <motion.span
                                                    initial={{ opacity: 0, y: 5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.15, duration: 0.2 }}
                                                >
                                                    Claimed Successfully!
                                                </motion.span>
                                            </motion.button>
                                        ) : claimFlowStep === 'failed' ? (
                                            <motion.div
                                                key="failed"
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                    x: [0, -8, 8, -8, 8, 0]
                                                }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                transition={{
                                                    opacity: { duration: 0.3 },
                                                    scale: { duration: 0.3 },
                                                    x: { duration: 0.4, delay: 0.1 }
                                                }}
                                                className="space-y-2"
                                            >
                                                <motion.button
                                                    className="w-full py-2.5 text-[14px] font-bold bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl shadow-lg flex items-center justify-center gap-2"
                                                    disabled
                                                >
                                                    <motion.div
                                                        initial={{ scale: 0, rotate: 180 }}
                                                        animate={{ scale: 1, rotate: 0 }}
                                                        transition={{ type: "spring", stiffness: 300, damping: 15 }}
                                                    >
                                                        <X className="w-5 h-5" strokeWidth={3} />
                                                    </motion.div>
                                                    <span>Claim Failed</span>
                                                </motion.button>
                                                {claimErrorMessage && (
                                                    <motion.p
                                                        initial={{ opacity: 0, y: -5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="text-xs text-red-500 text-center"
                                                    >
                                                        {claimErrorMessage}
                                                    </motion.p>
                                                )}
                                                <motion.button
                                                    onClick={() => {
                                                        setClaimFlowStep('idle')
                                                        setClaimErrorMessage('')
                                                    }}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.5 }}
                                                    className="w-full py-2 text-[12px] font-semibold text-neutral-600 hover:text-neutral-800 transition-colors"
                                                >
                                                    Click to try again
                                                </motion.button>
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="idle"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                            >
                                                <Button
                                                    onClick={handleClaim}
                                                    disabled={claimPending || claimConfirming || isSyncing || unclaimedTks <= 0 || (!isNonEvmSelected && !claimingEnabled) || (isNonEvmSelected && !claimAddressSaved)}
                                                    className="w-full py-2.5 text-[14px] font-bold bg-[#D4E157] hover:bg-[#C5D631] text-neutral-800 rounded-xl shadow-sm transition-all duration-200"
                                                >
                                                    {isSyncing ? (
                                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying Balance...</>
                                                    ) : claimPending ? (
                                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Waiting for transaction approval...</>
                                                    ) : claimConfirming ? (
                                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Claiming...</>
                                                    ) : isNonEvmSelected ? (
                                                        // Non-EVM users: No network switch needed, show claim on selected network
                                                        !claimAddressSaved ? (
                                                            "Set Claim Address First"
                                                        ) : unclaimedTks > 0 ? (
                                                            `Claim ${unclaimedTks.toLocaleString(undefined, { maximumFractionDigits: 2 })} TKS on ${selectedClaimNetwork.short}`
                                                        ) : (
                                                            "Claim"
                                                        )
                                                    ) : chainId !== selectedClaimNetwork.chainId ? (
                                                        // EVM users: Need network switch
                                                        `Switch to ${selectedClaimNetwork.short} & Claim`
                                                    ) : unclaimedTks > 0 ? (
                                                        `Claim ${unclaimedTks.toLocaleString(undefined, { maximumFractionDigits: 2 })} TKS`
                                                    ) : (
                                                        "Claim"
                                                    )}
                                                </Button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Non-EVM Claim Address Section for Solana/Tron/TON contributors */}
                            {selectedNetwork.isNonEvm && (
                                <div className="bg-white/60 rounded-xl p-3 border border-neutral-100 mb-2">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <Wallet className="h-3.5 w-3.5 text-[#558B2F]" />
                                        <p className="text-neutral-500 text-[11px] uppercase font-bold tracking-wider">
                                            EVM Claim Address
                                        </p>
                                        <AnimatePresence mode="wait">
                                            {claimAddressSaved && (
                                                <motion.button
                                                    key="edit-btn"
                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.8 }}
                                                    transition={{ duration: 0.2 }}
                                                    onClick={() => {
                                                        setClaimAddressSaved(false)
                                                        setClaimAddressError(null)
                                                    }}
                                                    className="ml-auto p-1 rounded-md hover:bg-neutral-100 transition-colors group"
                                                    title="Modify address"
                                                >
                                                    <svg
                                                        className="h-3.5 w-3.5 text-neutral-500 group-hover:text-[#558B2F] transition-colors"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        strokeWidth={2}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </motion.button>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    <p className="text-neutral-500 text-[11px] mb-2">
                                        Enter your BSC/ETH address to receive TKS tokens:
                                    </p>
                                    <div className="flex gap-2">
                                        <motion.input
                                            type="text"
                                            value={claimAddress}
                                            onChange={(e) => {
                                                if (!claimAddressSaved) {
                                                    setClaimAddress(e.target.value)
                                                    setClaimAddressError(null)
                                                }
                                            }}
                                            disabled={claimAddressSaved}
                                            placeholder="0x..."
                                            className={`flex-1 px-3 py-2 text-[12px] rounded-lg border focus:outline-none transition-all ${claimAddressSaved
                                                ? 'bg-neutral-50 border-[#D4E157]/50 text-neutral-600 cursor-not-allowed'
                                                : 'bg-white border-neutral-200 focus:border-[#D4E157] text-neutral-900'
                                                }`}
                                            animate={{
                                                backgroundColor: claimAddressSaved ? '#fafafa' : '#ffffff'
                                            }}
                                            transition={{ duration: 0.2 }}
                                        />
                                        <Button
                                            onClick={saveClaimAddress}
                                            disabled={claimAddressSaving || !claimAddress || claimAddressSaved}
                                            className={`px-3 py-2 rounded-lg text-[12px] font-semibold transition-all ${claimAddressSaved
                                                ? 'bg-[#D4E157]/20 text-[#558B2F]'
                                                : 'bg-[#558B2F] text-white hover:bg-[#4a7928]'
                                                } disabled:opacity-50`}
                                        >
                                            <AnimatePresence mode="wait">
                                                {claimAddressSaving ? (
                                                    <motion.div
                                                        key="loading"
                                                        initial={{ opacity: 0, rotate: -90 }}
                                                        animate={{ opacity: 1, rotate: 0 }}
                                                        exit={{ opacity: 0, rotate: 90 }}
                                                    >
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    </motion.div>
                                                ) : claimAddressSaved ? (
                                                    <motion.div
                                                        key="check"
                                                        initial={{ opacity: 0, scale: 0.5 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        exit={{ opacity: 0, scale: 0.5 }}
                                                    >
                                                        <Check className="h-4 w-4" />
                                                    </motion.div>
                                                ) : (
                                                    <motion.span
                                                        key="save"
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                    >
                                                        Save
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </Button>
                                    </div>
                                    <AnimatePresence mode="wait">
                                        {claimAddressError && (
                                            <motion.p
                                                initial={{ opacity: 0, y: -5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -5 }}
                                                className="text-red-500 text-[10px] mt-1.5"
                                            >
                                                {claimAddressError}
                                            </motion.p>
                                        )}
                                        {claimAddressSaved && (
                                            <motion.p
                                                initial={{ opacity: 0, y: -5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -5 }}
                                                className="text-[#558B2F] text-[10px] mt-1.5"
                                            >
                                                ✓ Address saved! TKS will be sent to: {claimAddress.slice(0, 8)}...{claimAddress.slice(-6)}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            <div className="flex items-center justify-between mt-1 text-neutral-500 text-[12px]">
                                <span>Your equity value:</span>
                                <div className="flex items-center gap-2">
                                    <span className="bg-[#D4E157]/20 text-neutral-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
                                        +{potentialGain}%
                                    </span>
                                    <span className="text-neutral-900 font-semibold">
                                        ${formatNumber(effectiveTotalBalance * TKS_STATIC.listingPrice)}
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Network selector and buy form */}
            <div className="bg-[#FAFAF8] rounded-[20px] p-3.5 border border-neutral-100 shadow-sm relative overflow-hidden min-h-[350px]">
                <AnimatePresence mode="wait">
                    {buyFlowStep === 'idle' ? (
                        <motion.div
                            key="idle"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="space-y-4"
                        >
                            {/* Network dropdown */}
                            <div className="relative">
                                <p className="text-neutral-500 text-[11px] uppercase font-bold tracking-wider mb-2 ml-1">Network</p>
                                <button
                                    onClick={() => setNetworkOpen(!networkOpen)}
                                    className="w-full flex items-center justify-between bg-white border border-neutral-100 rounded-[14px] px-4 py-3 hover:bg-neutral-50 transition-all duration-200"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-6 h-6 rounded-full overflow-hidden border border-neutral-100 p-0.5">
                                            <Image src={selectedNetwork.icon} alt="" width={24} height={24} className="rounded-full" unoptimized />
                                        </div>
                                        <span className="text-[14px] font-semibold text-neutral-900">{selectedNetwork.name}</span>
                                    </div>
                                    <ChevronsDown className="h-4 w-4 text-neutral-500" />
                                </button>

                                {networkOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[16px] shadow-xl border border-neutral-100 py-1.5 z-50 overflow-hidden">
                                        {NETWORKS.map((network) => (
                                            <button
                                                key={network.chainId}
                                                onClick={() => handleNetworkSwitch(network)}
                                                className={`w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 transition-colors ${selectedNetwork.chainId === network.chainId ? 'bg-[#D4E157]/10' : ''}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-6 h-6 rounded-full overflow-hidden border border-neutral-100 p-0.5">
                                                        <Image src={network.icon} alt="" width={24} height={24} className="rounded-full" unoptimized />
                                                    </div>
                                                    <span className={`text-[14px] ${selectedNetwork.chainId === network.chainId ? 'font-bold text-neutral-900' : 'text-neutral-600'}`}>{network.name}</span>
                                                </div>
                                                {selectedNetwork.chainId === network.chainId && <Check className="h-4 w-4 text-[#558B2F]" />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Token input section */}
                            <div className="space-y-3">
                                <div className="bg-white border border-neutral-100 rounded-[18px] p-4 focus-within:ring-2 focus-within:ring-[#D4E157]/30 transition-all duration-200">
                                    <div className="flex justify-between mb-2">
                                        <p className="text-neutral-500 text-[11px] uppercase font-bold tracking-wider">You Pay</p>
                                        <div className="flex gap-1.5">
                                            {[10, 50, 100].map((amount) => (
                                                <button
                                                    key={amount}
                                                    onClick={() => handleQuickBuy(amount)}
                                                    className="text-[10px] font-bold bg-neutral-100 hover:bg-[#D4E157]/20 text-neutral-600 px-2.5 py-1 rounded-full transition-colors"
                                                >
                                                    ${amount}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            value={payAmount}
                                            onChange={(e) => handlePayAmountChange(e.target.value)}
                                            placeholder="0.00"
                                            className="bg-transparent border-none p-0 text-[18px] font-bold text-neutral-900 placeholder:text-neutral-300 flex-1 min-w-0 focus:outline-none"
                                        />
                                        <div className="relative flex-shrink-0">
                                            <button
                                                onClick={() => setPayTokenOpen(!payTokenOpen)}
                                                className="flex items-center gap-1 bg-neutral-50 hover:bg-neutral-100 px-2 py-1.5 rounded-lg border border-neutral-100 transition-colors"
                                            >
                                                <Image src={selectedPayToken.icon} alt="" width={16} height={16} className="rounded-full" unoptimized />
                                                <span className="text-[12px] font-bold text-neutral-900">{selectedPayToken.symbol}</span>
                                                <ChevronsDown className="h-3 w-3 text-neutral-500 ml-0.5" />
                                            </button>

                                            {payTokenOpen && (
                                                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-neutral-100 py-1 z-50 w-[140px]">
                                                    {availableTokens.map((token) => (
                                                        <button
                                                            key={token.symbol}
                                                            onClick={() => {
                                                                setSelectedPayToken(token)
                                                                setPayTokenOpen(false)
                                                            }}
                                                            className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 text-[13px] text-neutral-900 transition-colors ${selectedPayToken.symbol === token.symbol ? 'bg-[#D4E157]/10 font-bold' : ''}`}
                                                        >
                                                            <Image src={token.icon} alt="" width={18} height={18} className="rounded-full" unoptimized />
                                                            <span>{token.symbol}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between mt-1 text-[11px] text-neutral-500">
                                        <span>Balance</span>
                                        <span>{displayBalance} {selectedPayToken.symbol}</span>
                                    </div>
                                </div>

                                <div className="bg-white border border-neutral-100 rounded-[18px] p-4 relative">
                                    <p className="text-neutral-500 text-[11px] uppercase font-bold tracking-wider mb-2">You Receive</p>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            value={receiveAmount}
                                            readOnly
                                            placeholder="0.00"
                                            className="bg-transparent border-none p-0 text-[18px] font-bold text-neutral-900 placeholder:text-neutral-300 w-full"
                                        />
                                        <div className="flex items-center gap-2 bg-[#D4E157]/20 px-3 py-2 rounded-xl border border-[#D4E157]/30">
                                            <div className="w-5 h-5 bg-[#D4E157] rounded-full flex items-center justify-center">
                                                <span className="text-[10px] font-black text-[#558B2F]">T</span>
                                            </div>
                                            <span className="text-[13px] font-bold text-neutral-800">TKS</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Promo code section */}
                            <div className="mb-2">
                                {promoValid ? (
                                    <div className="flex items-center justify-between bg-[#D4E157]/10 border border-[#D4E157]/30 rounded-xl px-3 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <Ticket className="h-4 w-4 text-[#558B2F]" />
                                            <span className="text-[12px] font-bold text-[#558B2F]">{promoCode}</span>
                                            <span className="text-[11px] text-neutral-500">+{promoBonus}% bonus</span>
                                        </div>
                                        <button onClick={handleRemovePromo} className="text-neutral-500 hover:text-neutral-600 transition-colors">
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        {!promoExpanded ? (
                                            <button
                                                onClick={() => setPromoExpanded(true)}
                                                className="text-[11px] text-neutral-600 hover:text-neutral-800 transition-colors flex items-center gap-1.5"
                                            >
                                                <Ticket className="h-3.5 w-3.5" />
                                                Have a promo code?
                                            </button>
                                        ) : (
                                            <div className="space-y-1.5">
                                                <div className="flex gap-1.5">
                                                    <input
                                                        type="text"
                                                        value={promoInput}
                                                        onChange={(e) => { setPromoInput(e.target.value); setPromoError(""); }}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                                                        placeholder="Enter promo code"
                                                        className="flex-1 bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[12px] font-medium text-neutral-900 placeholder:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#D4E157]/30 focus:border-[#D4E157]/50"
                                                    />
                                                    <button
                                                        onClick={handleApplyPromo}
                                                        disabled={promoLoading || !promoInput.trim()}
                                                        className="px-4 py-2 bg-neutral-800 hover:bg-neutral-900 disabled:bg-neutral-300 text-white text-[12px] font-bold rounded-lg transition-colors"
                                                    >
                                                        {promoLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
                                                    </button>
                                                    <button
                                                        onClick={() => { setPromoExpanded(false); setPromoInput(""); setPromoError(""); }}
                                                        className="px-2 py-2 text-neutral-500 hover:text-neutral-600 transition-colors"
                                                        title="Cancel"
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                {promoError && (
                                                    <p className="text-[11px] text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3" /> {promoError}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Buy button */}
                            {isConnected ? (
                                needsApproval ? (
                                    <Button
                                        onClick={() => approve(parseUnits(payAmount || "1000000", selectedPayToken.decimals))}
                                        disabled={approvePending || approveConfirming || !payAmount || parseFloat(payAmount) <= 0}
                                        className="w-full py-5 text-[15px] font-bold bg-[#D4E157] hover:bg-[#C5D631] text-neutral-800 rounded-[14px] mb-2 shadow-sm transition-all duration-200 hover:shadow-md"
                                    >
                                        {approvePending ? (
                                            <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Confirm in Wallet...</>
                                        ) : approveConfirming ? (
                                            <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Approving...</>
                                        ) : (
                                            `Approve ${selectedPayToken.symbol}`
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleBuy}
                                        disabled={isBuyPending || isBuyConfirming || !payAmount || parseFloat(payAmount) <= 0}
                                        className="w-full py-5 text-[15px] font-bold bg-[#D4E157] hover:bg-[#C5D631] text-neutral-800 rounded-[14px] mb-2 shadow-sm transition-all duration-200 hover:shadow-md"
                                    >
                                        Buy TKS
                                    </Button>
                                )
                            ) : (
                                <Button
                                    onClick={() => {
                                        // Context-aware: Open custom modal for non-EVM networks
                                        if (selectedNetwork.chainId === 'tron') {
                                            setTronModalOpen(true)
                                        } else if (selectedNetwork.chainId === 'ton') {
                                            setTonModalOpen(true)
                                        } else if (selectedNetwork.chainId === 'solana') {
                                            // Switch to Solana namespace before opening AppKit
                                            try { switchCaipNetwork(customSolanaNetwork) } catch (e) { console.warn('[PresaleBox] switchCaipNetwork Solana:', e) }
                                            connectWallet()
                                        } else {
                                            connectWallet()
                                        }
                                    }}
                                    className="w-full py-5 text-[15px] font-bold bg-neutral-800 hover:bg-neutral-900 text-white rounded-[14px] mb-2 shadow-sm transition-all duration-200"
                                >
                                    <Wallet className="h-5 w-5 mr-2" /> Connect Wallet to Buy
                                </Button>
                            )}

                            {/* How to buy link - moved inside */}
                            <div className="pt-2">
                                <Dialog open={howToBuyOpen} onOpenChange={setHowToBuyOpen}>
                                    <DialogTrigger asChild>
                                        <button className="w-full text-center text-neutral-500 hover:text-neutral-600 text-[12px] font-medium transition-colors">
                                            How to buy
                                        </button>
                                    </DialogTrigger>
                                    <DialogContent className="bg-white border-neutral-100 rounded-[20px] pt-10">
                                        <DialogClose className="absolute right-4 top-4 p-2 rounded-full hover:bg-neutral-100 transition-colors">
                                            <X className="h-5 w-5 text-neutral-500" />
                                        </DialogClose>
                                        <DialogHeader>
                                            <DialogTitle className="text-neutral-900">How to Buy TKS</DialogTitle>
                                            <DialogDescription className="text-neutral-600">Follow these simple steps</DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-4 text-neutral-600 text-sm">
                                            <div className="flex gap-3">
                                                <div className="w-6 h-6 rounded-full bg-[#D4E157] flex items-center justify-center text-xs font-bold text-neutral-800">1</div>
                                                <div>
                                                    <p className="font-semibold text-neutral-900">Connect Wallet</p>
                                                    <p>Click "Connect Wallet" button above</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-3">
                                                <div className="w-6 h-6 rounded-full bg-[#D4E157] flex items-center justify-center text-xs font-bold text-neutral-800">2</div>
                                                <div>
                                                    <p className="font-semibold text-neutral-900">Select Network</p>
                                                    <p>Choose BSC, Ethereum, Polygon, or Base</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-3">
                                                <div className="w-6 h-6 rounded-full bg-[#D4E157] flex items-center justify-center text-xs font-bold text-neutral-800">3</div>
                                                <div>
                                                    <p className="font-semibold text-neutral-900">Enter Amount</p>
                                                    <p>Use quick buttons or enter custom amount</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-3">
                                                <div className="w-6 h-6 rounded-full bg-[#D4E157] flex items-center justify-center text-xs font-bold text-neutral-800">4</div>
                                                <div>
                                                    <p className="font-semibold text-neutral-900">Buy TKS</p>
                                                    <p>Confirm transaction in your wallet</p>
                                                </div>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </motion.div>
                    ) : buyFlowStep === 'awaiting' ? (
                        <motion.div
                            key="awaiting"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="h-[380px] flex flex-col items-center justify-center text-center p-6"
                        >
                            <div className="w-24 h-24 relative mb-6">
                                <motion.div
                                    className="absolute inset-0 bg-[#D4E157]/20 rounded-full"
                                    animate={{ scale: [1, 1.4, 1] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-[#D4E157] rounded-full shadow-lg">
                                    <Wallet className="h-10 w-10 text-[#558B2F]" />
                                </div>
                            </div>
                            <h3 className="text-[20px] font-bold text-neutral-900 mb-2">Awaiting Approval</h3>
                            <p className="text-neutral-500 text-[14px] mb-6">
                                Please check your wallet and confirm the transaction to proceed.
                            </p>
                            <div className="flex items-center gap-2 text-[#D4E157] font-bold text-[14px] animate-pulse">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Waiting for user signature...</span>
                            </div>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setBuyFlowStep('idle')
                                    setIsBuyFlowActive(false)
                                    resetSolana()
                                }}
                                className="mt-8 text-neutral-500 hover:text-neutral-600"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" /> Cancel Request
                            </Button>
                        </motion.div>
                    ) : buyFlowStep === 'processing' ? (
                        <motion.div
                            key="processing"
                            initial={{ opacity: 0, rotateY: 180 }}
                            animate={{ opacity: 1, rotateY: 0 }}
                            className="h-[380px] flex flex-col items-center justify-center text-center p-6 bg-[#D4E157]/5 border-2 border-dashed border-[#D4E157] rounded-[24px]"
                        >
                            <div className="w-20 h-20 bg-white shadow-xl rounded-2xl flex items-center justify-center mb-6 animate-bounce">
                                <Loader2 className="h-10 w-10 text-[#D4E157] animate-spin" />
                            </div>
                            <h3 className="text-[20px] font-bold text-neutral-900 mb-2">Transaction Processing</h3>
                            <p className="text-neutral-500 text-[14px] mb-4">
                                Your contribution is being recorded on the blockchain. This usually takes a few seconds.
                            </p>
                            <div className="w-full bg-neutral-100 h-1.5 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-[#D4E157]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="h-[380px] flex flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#D4E157]/10 to-transparent rounded-[24px]"
                        >
                            <div className="relative mb-6">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="w-24 h-24 bg-[#D4E157] rounded-full flex items-center justify-center shadow-lg"
                                >
                                    <Check className="h-12 w-12 text-[#558B2F]" strokeWidth={3} />
                                </motion.div>
                                <motion.div
                                    className="absolute -top-2 -right-2"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                >
                                    <Sparkles className="h-8 w-8 text-yellow-500" />
                                </motion.div>
                                <motion.div
                                    className="absolute -bottom-2 -left-2"
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <PartyPopper className="h-8 w-8 text-orange-400" />
                                </motion.div>
                            </div>
                            <h3 className="text-[24px] font-bold text-neutral-900 mb-2">Thank you for your contribution!</h3>
                            <p className="text-neutral-500 text-[14px] mb-8">
                                Your TKS tokens have been successfully secured in the vault.
                            </p>
                            <Button
                                onClick={() => setBuyFlowStep('idle')}
                                className="bg-neutral-900 text-white px-8 py-6 rounded-2xl font-bold hover:bg-neutral-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
                            >
                                <Check className="h-5 w-5 mr-2" /> Buy Again
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Network Switch Confirmation Modal */}
            <AnimatePresence>
                {showNetworkSwitchModal && pendingNetwork && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                            onClick={() => setShowNetworkSwitchModal(false)}
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", duration: 0.3 }}
                            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md"
                        >
                            <div className="bg-white rounded-3xl p-6 shadow-2xl border border-neutral-200">
                                {/* Header */}
                                <div className="text-center mb-6">
                                    <h3 className="text-[22px] font-bold text-neutral-900 mb-2">
                                        Switch Network?
                                    </h3>
                                    <p className="text-neutral-500 text-[14px]">
                                        You're currently connected to <span className="font-semibold text-neutral-900">{selectedNetwork.name}</span>
                                    </p>
                                </div>

                                {/* Network Transition Visual */}
                                <div className="flex items-center justify-center gap-4 mb-6 py-6 bg-neutral-50 rounded-2xl">
                                    {/* Current Network */}
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center mb-2 border-2 border-neutral-200">
                                            <img
                                                src={selectedNetwork.icon}
                                                alt=""
                                                className="w-10 h-10 object-contain"
                                            />
                                        </div>
                                        <span className="text-[12px] font-medium text-neutral-600">{selectedNetwork.name}</span>
                                    </div>

                                    {/* Arrow */}
                                    <motion.div
                                        animate={{ x: [0, 5, 0] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                    >
                                        <ArrowRight className="w-6 h-6 text-[#D4E157]" />
                                    </motion.div>

                                    {/* Target Network */}
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center mb-2 border-2 border-[#D4E157]">
                                            <img
                                                src={pendingNetwork.icon}
                                                alt=""
                                                className="w-10 h-10 object-contain"
                                            />
                                        </div>
                                        <span className="text-[12px] font-medium text-neutral-900 font-bold">{pendingNetwork.name}</span>
                                    </div>
                                </div>

                                {/* Warning */}
                                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-[13px] font-medium text-orange-900 mb-1">
                                                This will disconnect your wallet
                                            </p>
                                            <p className="text-[12px] text-orange-700">
                                                You'll need to reconnect with a wallet that supports {pendingNetwork.name}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setShowNetworkSwitchModal(false)
                                            setPendingNetwork(null)
                                        }}
                                        className="flex-1 border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-xl py-6"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            if (!pendingNetwork) return

                                            // Close modal
                                            setShowNetworkSwitchModal(false)

                                            // Disconnect current wallet
                                            await disconnectWallet()

                                            // Update network state
                                            setSelectedNetwork(pendingNetwork)
                                            const isNonEvm = pendingNetwork.isNonEvm === true
                                            setIsNonEvmSelected(isNonEvm)

                                            // Sync claim network
                                            if (typeof pendingNetwork.chainId === 'number' && [97, 11155111].includes(pendingNetwork.chainId)) {
                                                setSelectedClaimNetwork(pendingNetwork)
                                            } else {
                                                setSelectedClaimNetwork(NETWORKS[0])
                                            }

                                            // Update available tokens
                                            const newTokens = PAY_TOKENS[pendingNetwork.chainId] || PAY_TOKENS[NETWORKS[0].chainId]
                                            setSelectedPayToken(newTokens[0])
                                            setPayAmount("")
                                            setReceiveAmount("")

                                            // Capture the target chain before clearing pendingNetwork
                                            const targetChainId = pendingNetwork.chainId

                                            // Clear pending network
                                            setPendingNetwork(null)

                                            // Open wallet connect modal after a small delay
                                            // Context-aware: use custom modal for non-EVM networks
                                            setTimeout(() => {
                                                if (targetChainId === 'tron') {
                                                    setTronModalOpen(true)
                                                } else if (targetChainId === 'ton') {
                                                    setTonModalOpen(true)
                                                } else if (targetChainId === 'solana') {
                                                    // Switch to Solana namespace before connecting
                                                    try { switchCaipNetwork(customSolanaNetwork) } catch (e) { console.warn('[PresaleBox] switchCaipNetwork Solana:', e) }
                                                    connectWallet()
                                                } else {
                                                    connectWallet()
                                                }
                                            }, 300)
                                        }}
                                        className="flex-1 bg-[#D4E157] text-[#558B2F] hover:bg-[#C5D52E] rounded-xl py-6 font-bold shadow-lg"
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        Switch Network
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Custom Tron Wallet Modal - uses AppKit for WalletConnect */}
            <TronWalletModal
                open={tronModalOpen}
                onClose={() => setTronModalOpen(false)}
            />

            {/* Custom TON Wallet Modal - uses TON Connect */}
            <TonWalletModal
                open={tonModalOpen}
                onClose={() => setTonModalOpen(false)}
            />
        </div >
    )
}
